/**
 * `BottomOverflowShadow` — the "more rows below" overflow fade for a vertically scrollable
 * body (MUI DataGrid `data-with-bottom-overlay` parity). The vertical twin of
 * `PinnedColumnShadows`.
 *
 * COLOUR: a gradient from the CARD's own background colour (opaque at the bottom edge) up to
 * transparent, so the last rows dissolve into the panel. It follows the table `surface`
 * (`bottomShadowGradientBySurface[surface]`) rather than a fixed black band — a hardcoded
 * black smudge read wrong on a lighter card, and "follow the background" is the correct
 * scroll-fade affordance on any surface.
 *
 * PLACEMENT: mount inside the NON-scrolling `position: relative` wrapper that bounds the
 * scroll container (the preset's card), NOT inside the scroll container and NEVER as a CSS
 * `mask` on it — a mask hides the container's scrollbars in Chromium (the bug that dropped
 * the old mask-based fade; see `dataTableRootVariants`). A single `absolute` band pinned to
 * the card's bottom edge, inset by the scrollbar dimensions so the band never paints over
 * the horizontal scrollbar (`bottom = scrollbarHeight`) or the vertical scrollbar
 * (`right = scrollbarWidth`) — keeping both bars fully visible was the whole point of
 * dropping the mask. `pointer-events-none` + the low `z-[1]` keep it a pure visual overlay:
 * above the scrolled cells, out of interaction.
 *
 * VISIBILITY: `show` (from `useVerticalOverflowShadow`) is `true` only while the body
 * overflows AND is not scrolled to the bottom — a body that fits, or is scrolled to its end,
 * shows nothing. The toggle is an opacity fade so the band eases in on the first scroll
 * instead of popping (mirrors the pinned rails).
 */
import { cn } from '@gto/ui/cn'

import {
  bottomShadowGradientBySurface,
  type DataTableSurface,
  DEFAULT_DATA_TABLE_SURFACE,
} from './data-table.styles'

/** Height (px) of the bottom overflow band — the gradient's fade distance. */
export const BOTTOM_SHADOW_HEIGHT_PX = 28

export type BottomOverflowShadowProps = {
  /** Show the fade — the body overflows vertically and is not scrolled to the bottom. */
  show: boolean
  /** The card surface the fade blends into. Defaults to `elevation2` (the DS default card). */
  surface?: DataTableSurface
  /**
   * Vertical-scrollbar width (px) from `useHorizontalOverflowShadows`. The band stops this
   * far short of the right edge so the fade never overlaps the vertical scrollbar. `0` with
   * overlay scrollbars (macOS) or no vertical overflow.
   */
  scrollbarWidth?: number
  /**
   * Horizontal-scrollbar height (px) from `useVerticalOverflowShadow`. The band sits this
   * far above the bottom edge so the fade never overlaps the horizontal scrollbar. `0` with
   * overlay scrollbars (macOS) or no horizontal overflow.
   */
  scrollbarHeight?: number
  /**
   * Height (px) of an opaque band pinned to the bottom of the scrollport — the summary band.
   * The fade sits ABOVE it, so it dissolves the last SCROLLING rows rather than washing over
   * a band that is not scrolling at all (the MUI original did the same, subtracting
   * `--DataGrid-bottomContainerHeight` from its mask). `0` when no band renders.
   */
  bottomInset?: number
}

/** Continuous bottom overflow fade. See the file header for colour, placement + visibility. */
export const BottomOverflowShadow = ({
  show,
  surface = DEFAULT_DATA_TABLE_SURFACE,
  scrollbarWidth = 0,
  scrollbarHeight = 0,
  bottomInset = 0,
}: BottomOverflowShadowProps) => (
  <div
    aria-hidden
    data-bottom-shadow=""
    className={cn(
      'pointer-events-none absolute left-0 z-[1] transition-opacity duration-200',
      bottomShadowGradientBySurface[surface],
      show ? 'opacity-100' : 'opacity-0',
    )}
    style={{
      bottom: scrollbarHeight + bottomInset,
      right: scrollbarWidth,
      height: BOTTOM_SHADOW_HEIGHT_PX,
    }}
  />
)
